/**
 * AI Consumption Console API layer (P3). Typed calls to the P3 backend
 * (`/admin/ai-usage/*`, gated ops.metrics.read) + budget writes via the shipped
 * `PUT /admin/config/{key}` (ops.app_config.write). Types mirror
 * `apps/api/garden/features/ai_usage/schemas.py`. i18n keys live in the shared
 * `admin` namespace (registerAdminI18n) — this module adds NO new namespace.
 */
import { apiClient, apiFetch } from "@/lib/api-client";

export type BudgetLevel = "under" | "approaching" | "over";
export type Granularity = "daily" | "monthly";
export type Dimension = "feature" | "model";

export interface SpendTrendPoint {
  bucket: string;
  cost_usd: number;
  tokens: number;
}
export interface SpendTrendResponse {
  window: Granularity;
  points: SpendTrendPoint[];
}
export interface BreakdownItem {
  key: string;
  cost_usd: number;
  tokens: number;
  share_pct: number;
}
export interface BreakdownResponse {
  dimension: Dimension;
  total_usd: number;
  items: BreakdownItem[];
}
export interface ByUserItem {
  user_id: string;
  cost_tiyin: number;
  cost_kgs: number;
  input_tokens: number;
  output_tokens: number;
}
export interface ByUserResponse {
  items: ByUserItem[];
}
export interface TodayGaugeResponse {
  day: string;
  cost_usd: number;
  tokens: number;
}
export interface CeilingsResponse {
  daily_ceiling_usd: number;
  monthly_ceiling_usd: number;
  daily_ceiling_tokens: number;
  monthly_ceiling_tokens: number;
}
export interface BudgetWindowStatus {
  window: Granularity;
  spend_usd: number;
  ceiling_usd: number;
  usd_status: BudgetLevel;
  spend_tokens: number;
  ceiling_tokens: number;
  tokens_status: BudgetLevel;
  threshold_pct: number;
}
export interface BudgetStatusResponse {
  daily: BudgetWindowStatus;
  monthly: BudgetWindowStatus;
  alert_threshold_pct: number;
  ceilings: CeilingsResponse;
}

/** Canonical app_config keys the budget form writes (mirror budget.py). */
export const BUDGET_KEYS = {
  dailyUsd: "llm.budget.daily_ceiling_usd",
  monthlyUsd: "llm.budget.monthly_ceiling_usd",
  dailyTokens: "llm.budget.daily_ceiling_tokens",
  monthlyTokens: "llm.budget.monthly_ceiling_tokens",
  alertPct: "llm.budget.alert_threshold_pct",
} as const;

export const aiUsageKeys = {
  trend: (window: Granularity, days: number) =>
    ["admin", "ai-usage", "trend", window, days] as const,
  byFeature: (days: number) => ["admin", "ai-usage", "by-feature", days] as const,
  byModel: (days: number) => ["admin", "ai-usage", "by-model", days] as const,
  byUser: (days: number, limit: number) =>
    ["admin", "ai-usage", "by-user", days, limit] as const,
  today: () => ["admin", "ai-usage", "today"] as const,
  budget: () => ["admin", "ai-usage", "budget"] as const,
} as const;

export function getTrend(params: { window: Granularity; days: number }): Promise<SpendTrendResponse> {
  const q = new URLSearchParams({ window: params.window, days: String(params.days) });
  return apiClient.get<SpendTrendResponse>(`/admin/ai-usage/trend?${q.toString()}`);
}
export function getByFeature(days: number): Promise<BreakdownResponse> {
  return apiClient.get<BreakdownResponse>(`/admin/ai-usage/by-feature?days=${days}`);
}
export function getByModel(days: number): Promise<BreakdownResponse> {
  return apiClient.get<BreakdownResponse>(`/admin/ai-usage/by-model?days=${days}`);
}
export function getByUser(days: number, limit: number): Promise<ByUserResponse> {
  return apiClient.get<ByUserResponse>(`/admin/ai-usage/by-user?days=${days}&limit=${limit}`);
}
export function getToday(): Promise<TodayGaugeResponse> {
  return apiClient.get<TodayGaugeResponse>("/admin/ai-usage/today");
}
export function getBudget(): Promise<BudgetStatusResponse> {
  return apiClient.get<BudgetStatusResponse>("/admin/ai-usage/budget");
}

export type ConfigValueType = "string" | "int" | "float" | "bool" | "json";
export interface AppConfigResponse {
  key: string;
  value: string;
  value_type: string;
  description: string | null;
  updated_at: string;
}

/** Write one budget ceiling via the shipped config endpoint (ops.app_config.write). */
export function putAppConfig(
  key: string,
  value: string,
  valueType: ConfigValueType,
): Promise<AppConfigResponse> {
  return apiFetch<AppConfigResponse, { value: string; value_type: ConfigValueType; description: string }>(
    {
      method: "PUT",
      path: `/admin/config/${key}`,
      body: { value, value_type: valueType, description: "LLM budget ceiling (AI console)" },
    },
  );
}
